import React, { Component } from "react"
import { loadStripe } from "@stripe/stripe-js"
import posthog from "posthog-js"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import ApiHandler from "src/api/ApiHandler"
import "./StripeCheckout.css"


interface StripeCheckoutProps {
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
    isLoggedIn: boolean
    onComplete: (paymentIntentId: string) => void
    onCancel: () => void
    onLoginRedirect: () => void
}

interface StripeCheckoutState {
    loading: boolean
    processing: boolean
    error: string | null
    alreadyLifetime: boolean
    amountCents: number | null
    paymentIntentId: string | null
}


class StripeCheckout extends Component<StripeCheckoutProps, StripeCheckoutState> {
    private cardRef: React.RefObject<HTMLDivElement>
    private stripe: any = null
    private elements: any = null
    private paymentElement: any = null
    private mounted = false

    constructor(props: StripeCheckoutProps) {
        super(props)
        this.state = {
            loading: true,
            processing: false,
            error: null,
            alreadyLifetime: false,
            amountCents: null,
            paymentIntentId: null,
        }
        this.cardRef = React.createRef()
    }

    async componentDidMount() {
        if (this.mounted) return
        this.mounted = true
        await this.initializePayment()
    }

    componentWillUnmount() {
        if (this.paymentElement) this.paymentElement.unmount()
    }

    async initializePayment() {
        try {
            const response = await this.props.apiHandler.createCheckout()
            if (!response?.client_secret || !response?.public_key) {
                this.setState({ loading: false, error: "Failed to create payment session" })
                return
            }
            this.setState({
                amountCents: response.amount ?? null,
                paymentIntentId: response.payment_intent_id ?? null,
            })

            this.stripe = await loadStripe(response.public_key)
            if (!this.stripe) {
                this.setState({ loading: false, error: "Failed to load payment provider" })
                return
            }

            this.elements = this.stripe.elements({
                clientSecret: response.client_secret,
                appearance: {
                    theme: "stripe",
                    variables: {
                        colorPrimary: "#1f2937",
                        borderRadius: "8px",
                        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    },
                },
            })

            this.paymentElement = this.elements.create("payment", {
                paymentMethodOrder: ["apple_pay", "google_pay", "card", "revolut_pay"],
            })

            if (this.cardRef.current) {
                this.paymentElement.mount(this.cardRef.current)
            }

            this.setState({ loading: false })
        } catch (error: any) {
            const message = error?.message || error?.error || "Something went wrong. Please try again."
            const isAlreadyLifetime = typeof message === "string" && message.toLowerCase().includes("you already have")
            console.error("Stripe setup error:", message)
            this.setState({ loading: false, error: message, alreadyLifetime: isAlreadyLifetime })
        }
    }

    handleSubmit = async () => {
        if (!this.stripe || !this.elements || this.state.processing) return

        posthog?.capture("pay_button_clicked", { payment_intent_id: this.state.paymentIntentId })
        this.setState({ processing: true, error: null })

        const returnUrl = new URL(`${window.location.origin}/plans`)
        returnUrl.searchParams.set("success", "true")
        if (this.state.paymentIntentId) {
            returnUrl.searchParams.set("pi", this.state.paymentIntentId)
        }

        const { error } = await this.stripe.confirmPayment({
            elements: this.elements,
            confirmParams: {
                return_url: returnUrl.toString(),
            },
            redirect: "if_required",
        })

        if (error) {
            this.setState({ processing: false, error: error.message })
        } else {
            this.setState({ processing: false })
            this.props.onComplete(this.state.paymentIntentId!)
        }
    }

    render() {
        return (
            <div className="stripe-checkout-container">
                {this.state.loading && (
                    <div className="stripe-checkout-loading">Loading payment...</div>
                )}
                {this.state.alreadyLifetime && (
                    <div className="stripe-checkout-lifetime-active">
                        <p className="stripe-checkout-lifetime-title">Lifetime Access Active</p>
                        <p className="stripe-checkout-lifetime-subtitle">You have full access to every feature, forever.</p>
                    </div>
                )}
                {this.state.error && !this.state.alreadyLifetime && (
                    <div className="stripe-checkout-error-message">
                        {this.state.error}
                    </div>
                )}
                <div ref={this.cardRef as React.RefObject<HTMLDivElement>} />
                {!this.state.loading && !this.state.alreadyLifetime && (
                    <div className="stripe-checkout-actions">
                        <button
                            className="stripe-checkout-pay"
                            onClick={this.handleSubmit}
                            disabled={this.state.processing}
                        >
                            {this.state.processing ? "Processing..." : this.state.amountCents ? `Pay €${(this.state.amountCents / 100).toFixed(0)}` : "Pay"}
                        </button>
                        <button
                            className="stripe-checkout-cancel"
                            onClick={this.props.onCancel}
                            disabled={this.state.processing}
                        >
                            Cancel
                        </button>
                    </div>
                )}
            </div>
        )
    }
}

export default StripeCheckout
